import { ArrowDown, ArrowUp, Settings, Trash } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import SelectWithSearch from "@/components/ui/select-search";
import SimpleSelect from "@/components/ui/simple-select";
import ColorInput from "@/components/ui/color-input";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import type { Field } from "@/type/common";
import { useContext } from "react";
import { useGraphConfig } from "@/store/contexts/GraphConfigContext";
import GraphConfigFormContext from "@/store/contexts/GraphConfigFormContext";

export interface TableColumnItem {
  field: string;
  header: string;
  width: number;
  sortable: boolean;
  total: "sum" | "avg" | "count" | "min" | "max";
  aggregation: "sum" | "avg" | "count" | "count_distinct" | "min" | "max";
  align: "left" | "center" | "right";
  color?: string;
  prefix?: string;
  suffix?: string;
  format?: "number" | "decimal" | "percent" | "currency" | "compact";
  decimals?: number;
}

interface ColumnsSelectorProps {}

export const ColumnsSelector = ({}: ColumnsSelectorProps) => {
  const { fields } = useGraphConfig();
  const { config, setConfig } = useContext(GraphConfigFormContext);

  if (!config) return null;

  const columns = config.table_options?.columns || [];

  const onChange = (newColumns: TableColumnItem[]) => {
    setConfig({
      ...config,
      table_options: {
        ...config.table_options,
        columns: newColumns,
      },
    });
  };
  const handleColumnChange = (
    index: number,
    updated: Partial<TableColumnItem>
  ) => {
    const next = columns.map((col, i) =>
      i === index ? { ...col, ...updated } : col
    );
    onChange(next);
  };

  const handleDelete = (index: number) => {
    const next = columns.filter((_, i) => i !== index);
    onChange(next);
  };

  const handleAdd = () => {
    // Add first available field not already used
    const used = new Set(columns.map((c) => c.field));
    const available = fields.find((f) => !used.has(f.field));
    if (!available) return;
    onChange([
      ...columns,
      {
        field: available.field,
        header: available.label || available.name,
        width: 120,
        sortable: true,
        total: "sum",
        aggregation: "sum",
        align: "left",
        color: "#000000",
      },
    ]);
  };

  // Move column up
  const handleMoveUp = (index: number) => {
    if (index <= 0) return;
    const next = [...columns];
    [next[index - 1], next[index]] = [next[index], next[index - 1]];
    onChange(next);
  };

  // Move column down
  const handleMoveDown = (index: number) => {
    if (index >= columns.length - 1) return;
    const next = [...columns];
    [next[index], next[index + 1]] = [next[index + 1], next[index]];
    onChange(next);
  };

  return (
    <div className="flex flex-col gap-4 pb-4">
      {columns.map((col, i) => (
        <Card className="w-full p-0" key={col.field}>
          <CardContent className="p-4">
            {/* Top row: Field selector, Delete, Configure */}
            <div className="flex justify-between items-center mb-2 gap-2">
              <div className="flex flex-row items-center gap-2 flex-1">
                <div className="flex flex-row gap-2">
                  {/* Only show Up if not first */}
                  {i > 0 && (
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleMoveUp(i)}
                      title="Move up"
                    >
                      <ArrowUp />
                    </Button>
                  )}
                  {/* Only show Down if not last */}
                  {i < columns.length - 1 && (
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleMoveDown(i)}
                      title="Move down"
                    >
                      <ArrowDown />
                    </Button>
                  )}
                </div>
                <div className="w-1/2 min-w-[120px]">
                  <SelectWithSearch
                    value={col.field}
                    options={fields as any}
                    placeholder="Select field..."
                    onChange={(value) => {
                      const field = fields.find((f: Field) => f.field === value);
                      handleColumnChange(i, {
                        field: value,
                        header: field?.label || field?.name || value,
                      });
                    }}
                  />
                </div>
              </div>
              <div className="flex flex-row items-center">
                <Dialog>
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      title="Configure column"
                    >
                      <Settings />
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>Configure Column</DialogTitle>
                    </DialogHeader>
                    <div className="flex flex-col gap-4 mt-2">
                      {/* Row 1: Width and Align */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="flex flex-col">
                          <Label className="mb-1">Width</Label>
                          <Input
                            type="number"
                            value={col.width || 120}
                            min={40}
                            max={1000}
                            onChange={(e) =>
                              handleColumnChange(i, {
                                width: Number(e.target.value),
                              })
                            }
                            placeholder="Width"
                            className="w-full"
                          />
                        </div>
                        <div className="flex flex-col">
                          <Label className="mb-1">Align</Label>
                          <SimpleSelect
                            value={col.align}
                            options={[
                              { label: "Left", value: "left" },
                              { label: "Center", value: "center" },
                              { label: "Right", value: "right" },
                            ]}
                            onChange={(value) =>
                              handleColumnChange(i, { align: value as any })
                            }
                          />
                        </div>
                      </div>

                      {/* Row 2: Aggregation (full width) */}
                      <div className="flex flex-col">
                        <Label className="mb-1">Aggregation</Label>
                        <SimpleSelect
                          placeholder="Aggregation"
                          value={col.aggregation}
                          options={[
                            { label: "Sum", value: "sum" },
                            { label: "Avg", value: "avg" },
                            { label: "Count", value: "count" },
                            {
                              label: "Count Distinct",
                              value: "count_distinct",
                            },
                            { label: "Min", value: "min" },
                            { label: "Max", value: "max" },
                          ]}
                          onChange={(value) =>
                            handleColumnChange(i, { aggregation: value as any })
                          }
                        />
                      </div>

                      <Separator />

                      {/* Row 3: Prefix and Suffix */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="flex flex-col">
                          <Label className="mb-1">Prefix</Label>
                          <Input
                            type="text"
                            value={col.prefix || ""}
                            onChange={(e) =>
                              handleColumnChange(i, { prefix: e.target.value })
                            }
                            placeholder="e.g., $, €"
                            className="w-full"
                          />
                        </div>
                        <div className="flex flex-col">
                          <Label className="mb-1">Suffix</Label>
                          <Input
                            type="text"
                            value={col.suffix || ""}
                            onChange={(e) =>
                              handleColumnChange(i, { suffix: e.target.value })
                            }
                            placeholder="e.g., %, kg, units"
                            className="w-full"
                          />
                        </div>
                      </div>

                      {/* Row 4: Format and Decimals */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="flex flex-col">
                          <Label className="mb-1">Format</Label>
                          <SimpleSelect
                            value={col.format || "number"}
                            options={[
                              { label: "Number", value: "number" },
                              { label: "Decimal", value: "decimal" },
                              { label: "Percent", value: "percent" },
                              { label: "Currency", value: "currency" },
                              { label: "Compact", value: "compact" },
                            ]}
                            onChange={(value) =>
                              handleColumnChange(i, { format: value as any })
                            }
                          />
                        </div>
                        <div className="flex flex-col">
                          <Label className="mb-1">Decimals</Label>
                          <Input
                            type="number"
                            value={col.decimals ?? 2}
                            min={0}
                            max={10}
                            onChange={(e) =>
                              handleColumnChange(i, {
                                decimals: Number(e.target.value),
                              })
                            }
                            placeholder="Number of decimals"
                            className="w-full"
                          />
                        </div>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 ml-2"
                  onClick={() => handleDelete(i)}
                  title="Delete column"
                >
                  <Trash className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            </div>
            <Separator className="my-4" />
            {/* Bottom row: only the column name field */}
            <div className="flex flex-col">
              <Input
                value={col.header}
                onChange={(e) =>
                  handleColumnChange(i, { header: e.target.value })
                }
                placeholder="Header"
              />
            </div>
          </CardContent>
        </Card>
      ))}
      <Button variant="outline" className="w-full mt-2" onClick={handleAdd}>
        Add Column
      </Button>
    </div>
  );
};

export default ColumnsSelector;
